/**
 * Phantom Screens Dashboard Page
 *
 * Manufacturing and dealer network operations dashboard for Phantom Screens.
 * Shows DoD/WoW/MoM/QoQ/YoY comparisons for key manufacturing and distribution KPIs.
 * Uses comprehensive data from phantomData.ts for SLM training compatibility.
 * Founded 1992, Abbotsford BC - retractable screen manufacturer with dealer network.
 */
import { useState } from 'react';
import {
  Card,
  Text,
  Group,
  Stack,
  Badge,
  SimpleGrid,
  RingProgress,
  Table,
  Select,
  Accordion,
} from '@mantine/core';
import {
  IconTruck,
  IconBuildingFactory2,
  IconUsers,
  IconClock,
  IconChartBar,
  IconPackage,
  IconMapPin,
  IconTrendingUp,
  IconTrendingDown,
  IconMinus,
} from '@tabler/icons-react';
import TopNav from '../components/TopNav';
import phantomLogo from '../assets/logos/phantom-screens-logo.png';
import {
  PHANTOM_REGIONS,
  PHANTOM_METRICS,
  PHANTOM_PRODUCTS,
  getTopDealers,
  getBottomDealers,
  type TimePeriod,
} from '../data/phantomData';

const formatCurrency = (value: number, compact = false): string => {
  if (compact) {
    if (value >= 1000000000) return `$${(value / 1000000000).toFixed(1)}B`;
    if (value >= 1000000) return `$${(value / 1000000).toFixed(1)}M`;
    if (value >= 1000) return `$${(value / 1000).toFixed(0)}K`;
  }
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(value);
};

const formatChange = (value: number, invert = false) => {
  const isPositive = invert ? value < 0 : value > 0;
  const isNeutral = value === 0;
  const color = isPositive ? 'green' : isNeutral ? 'gray' : 'red';
  const Icon = isPositive ? IconTrendingUp : isNeutral ? IconMinus : IconTrendingDown;

  return (
    <Group gap={4}>
      <Icon size={14} color={isPositive ? '#16a34a' : isNeutral ? '#6b7280' : '#dc2626'} />
      <Text size="sm" c={color} fw={500}>
        {value > 0 ? '+' : ''}{value.toFixed(1)}%
      </Text>
    </Group>
  );
};

const formatChangeAbs = (value: number, invert = false) => {
  const isPositive = invert ? value < 0 : value > 0;
  const color = isPositive ? 'green' : value === 0 ? 'gray' : 'red';
  const prefix = value > 0 ? '+' : '';
  return (
    <Text span c={color} fw={500} size="sm">
      {prefix}{value}
    </Text>
  );
};

export default function PhantomScreensPage() {
  const [selectedPeriod, setSelectedPeriod] = useState<TimePeriod>('MoM');

  const topDealers = getTopDealers(5);
  const bottomDealers = getBottomDealers(5);

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        display: 'flex',
        flexDirection: 'column',
        background: '#f8f9fa',
      }}
    >
      <TopNav />

      <div style={{ flex: 1, overflow: 'auto', padding: 24 }}>
        <Stack gap="lg">
          {/* Header with Logo */}
          <Group justify="space-between" align="flex-start">
            <div>
              <Group gap="md" align="center">
                <img src={phantomLogo} alt="Phantom Screens" style={{ height: 48, width: 'auto' }} />
                <div>
                  <Text size="sm" c="dimmed" style={{ fontFamily: 'Inter, sans-serif' }}>{PHANTOM_METRICS.totalDealers} dealers across North America | Since 1992</Text>
                </div>
              </Group>
            </div>
            <Group gap="md">
              <Select
                value={selectedPeriod}
                onChange={(v) => setSelectedPeriod(v as TimePeriod)}
                data={[
                  { value: 'DoD', label: 'Day over Day' },
                  { value: 'WoW', label: 'Week over Week' },
                  { value: 'MoM', label: 'Month over Month' },
                  { value: 'QoQ', label: 'Quarter over Quarter' },
                  { value: 'YoY', label: 'Year over Year' },
                ]}
                size="sm"
                w={180}
              />
            </Group>
          </Group>

          {/* Key Financial Metrics */}
          <SimpleGrid cols={{ base: 2, sm: 3, md: 5 }}>
            <Card withBorder padding="lg" radius="md">
              <Group justify="space-between" mb="xs">
                <Text size="xs" c="dimmed" tt="uppercase" fw={600}>Revenue</Text>
                <IconChartBar size={20} color="#6b7280" />
              </Group>
              <Text size="xl" fw={700}>{formatCurrency(PHANTOM_METRICS.totalRevenue.current, true)}</Text>
              <Group gap="xs" mt="xs">
                <Text size="xs" c="dimmed">{selectedPeriod}:</Text>
                {formatChange(PHANTOM_METRICS.totalRevenue[selectedPeriod])}
              </Group>
            </Card>

            <Card withBorder padding="lg" radius="md">
              <Group justify="space-between" mb="xs">
                <Text size="xs" c="dimmed" tt="uppercase" fw={600}>EBITDA</Text>
                <IconTrendingUp size={20} color="#6b7280" />
              </Group>
              <Text size="xl" fw={700}>{formatCurrency(PHANTOM_METRICS.ebitda.current, true)}</Text>
              <Group gap="xs" mt="xs">
                <Text size="xs" c="dimmed">{selectedPeriod}:</Text>
                {formatChange(PHANTOM_METRICS.ebitda[selectedPeriod])}
              </Group>
            </Card>

            <Card withBorder padding="lg" radius="md">
              <Group justify="space-between" mb="xs">
                <Text size="xs" c="dimmed" tt="uppercase" fw={600}>Units Shipped</Text>
                <IconPackage size={20} color="#6b7280" />
              </Group>
              <Text size="xl" fw={700}>{PHANTOM_METRICS.unitsShipped.current.toLocaleString()}</Text>
              <Group gap="xs" mt="xs">
                <Text size="xs" c="dimmed">{selectedPeriod}:</Text>
                {formatChange(PHANTOM_METRICS.unitsShipped[selectedPeriod])}
              </Group>
            </Card>

            <Card withBorder padding="lg" radius="md">
              <Group justify="space-between" mb="xs">
                <Text size="xs" c="dimmed" tt="uppercase" fw={600}>Active Dealers</Text>
                <IconUsers size={20} color="#6b7280" />
              </Group>
              <Text size="xl" fw={700}>{PHANTOM_METRICS.totalDealers}</Text>
              <Group gap="xs" mt="xs">
                <Text size="xs" c="dimmed">{selectedPeriod}:</Text>
                {formatChangeAbs(selectedPeriod === 'DoD' ? 0 : selectedPeriod === 'WoW' ? 1 : selectedPeriod === 'MoM' ? 3 : selectedPeriod === 'QoQ' ? 8 : 22)}
              </Group>
            </Card>

            <Card withBorder padding="lg" radius="md">
              <Group justify="space-between" mb="xs">
                <Text size="xs" c="dimmed" tt="uppercase" fw={600}>Lead Time</Text>
                <IconClock size={20} color="#6b7280" />
              </Group>
              <Text size="xl" fw={700}>{PHANTOM_METRICS.leadTimeDays.current} days</Text>
              <Group gap="xs" mt="xs">
                <Text size="xs" c="dimmed">{selectedPeriod}:</Text>
                {formatChange(PHANTOM_METRICS.leadTimeDays[selectedPeriod], true)}
              </Group>
            </Card>
          </SimpleGrid>

          {/* Manufacturing KPIs */}
          <Card withBorder padding="lg" radius="md">
            <Text size="md" fw={600} mb="md" ta="center">Manufacturing Performance</Text>
            <Group justify="center" mb="md">
              <IconBuildingFactory2 size={20} color="#6b7280" />
            </Group>
            <SimpleGrid cols={{ base: 2, md: 4 }}>
              <div style={{ textAlign: 'center' }}>
                <RingProgress
                  size={120}
                  thickness={10}
                  sections={[{ value: PHANTOM_METRICS.otif.current, color: '#22c55e' }]}
                  label={
                    <Text ta="center" size="lg" fw={700}>
                      {PHANTOM_METRICS.otif.current}%
                    </Text>
                  }
                />
                <Text size="sm" fw={500} mt="xs">OTIF Rate</Text>
                <Text size="xs" c="dimmed">On-Time In-Full</Text>
                <Group justify="center" mt={4}>
                  {formatChange(PHANTOM_METRICS.otif[selectedPeriod])}
                </Group>
              </div>
              <div style={{ textAlign: 'center' }}>
                <RingProgress
                  size={120}
                  thickness={10}
                  sections={[{ value: PHANTOM_METRICS.fillRate.current, color: '#3182ce' }]}
                  label={
                    <Text ta="center" size="lg" fw={700}>
                      {PHANTOM_METRICS.fillRate.current}%
                    </Text>
                  }
                />
                <Text size="sm" fw={500} mt="xs">Fill Rate</Text>
                <Text size="xs" c="dimmed">Order fulfillment</Text>
                <Group justify="center" mt={4}>
                  {formatChange(PHANTOM_METRICS.fillRate[selectedPeriod])}
                </Group>
              </div>
              <div style={{ textAlign: 'center' }}>
                <RingProgress
                  size={120}
                  thickness={10}
                  sections={[{ value: PHANTOM_METRICS.capacityUtilization.current, color: '#f59e0b' }]}
                  label={
                    <Text ta="center" size="lg" fw={700}>
                      {PHANTOM_METRICS.capacityUtilization.current}%
                    </Text>
                  }
                />
                <Text size="sm" fw={500} mt="xs">Capacity</Text>
                <Text size="xs" c="dimmed">Utilization</Text>
                <Group justify="center" mt={4}>
                  {formatChange(PHANTOM_METRICS.capacityUtilization[selectedPeriod])}
                </Group>
              </div>
              <div style={{ textAlign: 'center' }}>
                <RingProgress
                  size={120}
                  thickness={10}
                  sections={[{ value: PHANTOM_METRICS.firstPassYield.current, color: '#8b5cf6' }]}
                  label={
                    <Text ta="center" size="lg" fw={700}>
                      {PHANTOM_METRICS.firstPassYield.current}%
                    </Text>
                  }
                />
                <Text size="sm" fw={500} mt="xs">First Pass Yield</Text>
                <Text size="xs" c="dimmed">Quality rate</Text>
                <Group justify="center" mt={4}>
                  {formatChange(PHANTOM_METRICS.firstPassYield[selectedPeriod])}
                </Group>
              </div>
            </SimpleGrid>
          </Card>

          {/* Regional Performance with Expandable Dealers */}
          <Card withBorder padding="lg" radius="md">
            <Text size="md" fw={600} mb="md" ta="center">Regional Performance</Text>
            <Accordion chevronPosition="left" variant="separated">
              {PHANTOM_REGIONS.map((region) => (
                <Accordion.Item key={region.region} value={region.region}>
                  <Accordion.Control>
                    <Stack gap={0} style={{ flex: 1 }}>
                      <Group gap="xs">
                        <IconMapPin size={16} color="#6b7280" />
                        <Text fw={600} size="sm">{region.region}</Text>
                        <Badge size="sm" variant="light">{region.dealerCount} dealers</Badge>
                      </Group>
                      <Group justify="flex-end" gap="xl" style={{ width: '100%', marginTop: '8px' }}>
                        <Stack gap={0} align="flex-end" style={{ minWidth: '120px' }}>
                          <Text size="xs" c="dimmed">Revenue</Text>
                          <Text size="sm" fw={600}>{formatCurrency(region.revenue.current, true)}</Text>
                        </Stack>
                        <Stack gap={0} align="flex-end" style={{ minWidth: '140px' }}>
                          <Text size="xs" c="dimmed">Growth Rate</Text>
                          <Badge color={region.revenue[selectedPeriod] > 3.5 ? 'green' : region.revenue[selectedPeriod] > 2.5 ? 'yellow' : 'blue'} variant="light">
                            +{region.revenue[selectedPeriod]}% {selectedPeriod}
                          </Badge>
                        </Stack>
                        <Stack gap={0} align="flex-end" style={{ minWidth: '110px' }}>
                          <Text size="xs" c="dimmed">OTIF Rate</Text>
                          <Badge color={region.otifRate >= 95 ? 'green' : region.otifRate >= 94 ? 'yellow' : 'red'} variant="light">
                            {region.otifRate}%
                          </Badge>
                        </Stack>
                      </Group>
                    </Stack>
                  </Accordion.Control>
                  <Accordion.Panel>
                    <Stack gap="sm">
                      <Text size="sm" fw={600} c="dimmed" tt="uppercase">Dealer Performance Details</Text>
                      <Table striped highlightOnHover>
                        <Table.Thead>
                          <Table.Tr>
                            <Table.Th>Dealer</Table.Th>
                            <Table.Th>City</Table.Th>
                            <Table.Th style={{ textAlign: 'right' }}>Revenue</Table.Th>
                            <Table.Th style={{ textAlign: 'right' }}>{selectedPeriod}</Table.Th>
                            <Table.Th style={{ textAlign: 'right' }}>Units</Table.Th>
                            <Table.Th style={{ textAlign: 'right' }}>OTIF</Table.Th>
                            <Table.Th style={{ textAlign: 'right' }}>Level</Table.Th>
                          </Table.Tr>
                        </Table.Thead>
                      <Table.Tbody>
                        {region.dealers.slice(0, 10).map((dealer) => (
                          <Table.Tr key={dealer.id}>
                            <Table.Td>
                              <Group gap="xs">
                                {dealer.isTopPerformer && <Badge size="xs" color="green">Top</Badge>}
                                {dealer.isBottomPerformer && <Badge size="xs" color="red">Low</Badge>}
                                <Text size="sm">{dealer.name}</Text>
                              </Group>
                            </Table.Td>
                            <Table.Td><Text size="sm">{dealer.city}</Text></Table.Td>
                            <Table.Td style={{ textAlign: 'right' }}>{formatCurrency(dealer.revenue.current, true)}</Table.Td>
                            <Table.Td style={{ textAlign: 'right' }}>{formatChange(dealer.revenue[selectedPeriod])}</Table.Td>
                            <Table.Td style={{ textAlign: 'right' }}>{dealer.unitsInstalled.current.toLocaleString()}</Table.Td>
                            <Table.Td style={{ textAlign: 'right', display: 'flex', justifyContent: 'flex-end' }}>
                              <Badge color={dealer.otifRate >= 96 ? 'green' : dealer.otifRate >= 94 ? 'yellow' : 'red'}>
                                {dealer.otifRate}%
                              </Badge>
                            </Table.Td>
                            <Table.Td style={{ textAlign: 'right', display: 'flex', justifyContent: 'flex-end' }}>
                              <Badge color={dealer.certificationLevel === 'Gold' ? 'yellow' : dealer.certificationLevel === 'Silver' ? 'gray' : dealer.certificationLevel === 'Bronze' ? 'orange' : 'blue'} variant="light">
                                {dealer.certificationLevel}
                              </Badge>
                            </Table.Td>
                          </Table.Tr>
                        ))}
                      </Table.Tbody>
                      </Table>
                      {region.dealers.length > 10 && (
                        <Text size="xs" c="dimmed" mt="xs" ta="center">
                          Showing 10 of {region.dealers.length} dealers
                        </Text>
                      )}
                    </Stack>
                  </Accordion.Panel>
                </Accordion.Item>
              ))}
            </Accordion>
          </Card>

          <SimpleGrid cols={{ base: 1, md: 2 }}>
            {/* Top Dealers */}
            <Card withBorder padding="lg" radius="md">
              <Text size="md" fw={600} mb="md" ta="center">Top Performing Dealers</Text>
              <Group justify="flex-end" mb="md">
                <Badge color="green">By Revenue</Badge>
              </Group>
              <Table>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>#</Table.Th>
                    <Table.Th>Dealer</Table.Th>
                    <Table.Th style={{ textAlign: 'right' }}>Revenue</Table.Th>
                    <Table.Th style={{ textAlign: 'right' }}>{selectedPeriod}</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {topDealers.map((dealer, idx) => (
                    <Table.Tr key={dealer.id}>
                      <Table.Td>
                        <Badge color={idx === 0 ? 'yellow' : 'gray'} size="sm">#{idx + 1}</Badge>
                      </Table.Td>
                      <Table.Td>
                        <div>
                          <Text size="sm" fw={500}>{dealer.name}</Text>
                          <Text size="xs" c="dimmed">{dealer.city}, {dealer.stateProvince}</Text>
                        </div>
                      </Table.Td>
                      <Table.Td style={{ textAlign: 'right' }}>{formatCurrency(dealer.revenue.current, true)}</Table.Td>
                      <Table.Td style={{ textAlign: 'right' }}>{formatChange(dealer.revenue[selectedPeriod])}</Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </Card>

            {/* Bottom Dealers */}
            <Card withBorder padding="lg" radius="md">
              <Text size="md" fw={600} mb="md" ta="center">Needs Attention</Text>
              <Group justify="flex-end" mb="md">
                <Badge color="red">By Revenue</Badge>
              </Group>
              <Table>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>#</Table.Th>
                    <Table.Th>Dealer</Table.Th>
                    <Table.Th style={{ textAlign: 'right' }}>Revenue</Table.Th>
                    <Table.Th style={{ textAlign: 'right' }}>{selectedPeriod}</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {bottomDealers.map((dealer, idx) => (
                    <Table.Tr key={dealer.id}>
                      <Table.Td>
                        <Badge color="red" size="sm">#{idx + 1}</Badge>
                      </Table.Td>
                      <Table.Td>
                        <div>
                          <Text size="sm" fw={500}>{dealer.name}</Text>
                          <Text size="xs" c="dimmed">{dealer.city}, {dealer.stateProvince}</Text>
                        </div>
                      </Table.Td>
                      <Table.Td style={{ textAlign: 'right' }}>{formatCurrency(dealer.revenue.current, true)}</Table.Td>
                      <Table.Td style={{ textAlign: 'right' }}>{formatChange(dealer.revenue[selectedPeriod])}</Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </Card>
          </SimpleGrid>

          {/* Product Line Performance */}
          <Card withBorder padding="lg" radius="md">
            <Text size="md" fw={600} mb="md" ta="center">Product Line Performance</Text>
            <Group justify="center" mb="md">
              <IconTruck size={20} color="#6b7280" />
            </Group>
            <Table striped highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Product</Table.Th>
                  <Table.Th style={{ textAlign: 'right' }}>Units</Table.Th>
                  <Table.Th style={{ textAlign: 'right' }}>Revenue</Table.Th>
                  <Table.Th style={{ textAlign: 'right' }}>Margin</Table.Th>
                  <Table.Th style={{ textAlign: 'right' }}>{selectedPeriod}</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {PHANTOM_PRODUCTS.map((product) => (
                  <Table.Tr key={product.name}>
                    <Table.Td><Text size="sm" fw={500}>{product.name}</Text></Table.Td>
                    <Table.Td style={{ textAlign: 'right' }}>{product.units.current.toLocaleString()}</Table.Td>
                    <Table.Td style={{ textAlign: 'right' }}>{formatCurrency(product.revenue.current, true)}</Table.Td>
                    <Table.Td style={{ textAlign: 'right' }}>{product.margin}%</Table.Td>
                    <Table.Td style={{ textAlign: 'right' }}>{formatChange(product.revenue[selectedPeriod])}</Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Card>

          {/* Performance Trends */}
          <Card withBorder padding="lg" radius="md">
            <Text size="md" fw={600} mb="md" ta="center">Performance Trends</Text>
            <Table striped highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Metric</Table.Th>
                  <Table.Th style={{ textAlign: 'right' }}>Current</Table.Th>
                  <Table.Th style={{ textAlign: 'right' }}>DoD</Table.Th>
                  <Table.Th style={{ textAlign: 'right' }}>WoW</Table.Th>
                  <Table.Th style={{ textAlign: 'right' }}>MoM</Table.Th>
                  <Table.Th style={{ textAlign: 'right' }}>QoQ</Table.Th>
                  <Table.Th style={{ textAlign: 'right' }}>YoY</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                <Table.Tr>
                  <Table.Td><Text fw={500}>Revenue</Text></Table.Td>
                  <Table.Td style={{ textAlign: 'right' }}>{formatCurrency(PHANTOM_METRICS.totalRevenue.current, true)}</Table.Td>
                  <Table.Td style={{ textAlign: 'right' }}>{formatChange(PHANTOM_METRICS.totalRevenue.DoD)}</Table.Td>
                  <Table.Td style={{ textAlign: 'right' }}>{formatChange(PHANTOM_METRICS.totalRevenue.WoW)}</Table.Td>
                  <Table.Td style={{ textAlign: 'right' }}>{formatChange(PHANTOM_METRICS.totalRevenue.MoM)}</Table.Td>
                  <Table.Td style={{ textAlign: 'right' }}>{formatChange(PHANTOM_METRICS.totalRevenue.QoQ)}</Table.Td>
                  <Table.Td style={{ textAlign: 'right' }}>{formatChange(PHANTOM_METRICS.totalRevenue.YoY)}</Table.Td>
                </Table.Tr>
                <Table.Tr>
                  <Table.Td><Text fw={500}>EBITDA</Text></Table.Td>
                  <Table.Td style={{ textAlign: 'right' }}>{formatCurrency(PHANTOM_METRICS.ebitda.current, true)}</Table.Td>
                  <Table.Td style={{ textAlign: 'right' }}>{formatChange(PHANTOM_METRICS.ebitda.DoD)}</Table.Td>
                  <Table.Td style={{ textAlign: 'right' }}>{formatChange(PHANTOM_METRICS.ebitda.WoW)}</Table.Td>
                  <Table.Td style={{ textAlign: 'right' }}>{formatChange(PHANTOM_METRICS.ebitda.MoM)}</Table.Td>
                  <Table.Td style={{ textAlign: 'right' }}>{formatChange(PHANTOM_METRICS.ebitda.QoQ)}</Table.Td>
                  <Table.Td style={{ textAlign: 'right' }}>{formatChange(PHANTOM_METRICS.ebitda.YoY)}</Table.Td>
                </Table.Tr>
                <Table.Tr>
                  <Table.Td><Text fw={500}>Units Shipped</Text></Table.Td>
                  <Table.Td style={{ textAlign: 'right' }}>{PHANTOM_METRICS.unitsShipped.current.toLocaleString()}</Table.Td>
                  <Table.Td style={{ textAlign: 'right' }}>{formatChange(PHANTOM_METRICS.unitsShipped.DoD)}</Table.Td>
                  <Table.Td style={{ textAlign: 'right' }}>{formatChange(PHANTOM_METRICS.unitsShipped.WoW)}</Table.Td>
                  <Table.Td style={{ textAlign: 'right' }}>{formatChange(PHANTOM_METRICS.unitsShipped.MoM)}</Table.Td>
                  <Table.Td style={{ textAlign: 'right' }}>{formatChange(PHANTOM_METRICS.unitsShipped.QoQ)}</Table.Td>
                  <Table.Td style={{ textAlign: 'right' }}>{formatChange(PHANTOM_METRICS.unitsShipped.YoY)}</Table.Td>
                </Table.Tr>
                <Table.Tr>
                  <Table.Td><Text fw={500}>OTIF Rate</Text></Table.Td>
                  <Table.Td style={{ textAlign: 'right' }}>{PHANTOM_METRICS.otif.current}%</Table.Td>
                  <Table.Td style={{ textAlign: 'right' }}>{formatChange(PHANTOM_METRICS.otif.DoD)}</Table.Td>
                  <Table.Td style={{ textAlign: 'right' }}>{formatChange(PHANTOM_METRICS.otif.WoW)}</Table.Td>
                  <Table.Td style={{ textAlign: 'right' }}>{formatChange(PHANTOM_METRICS.otif.MoM)}</Table.Td>
                  <Table.Td style={{ textAlign: 'right' }}>{formatChange(PHANTOM_METRICS.otif.QoQ)}</Table.Td>
                  <Table.Td style={{ textAlign: 'right' }}>{formatChange(PHANTOM_METRICS.otif.YoY)}</Table.Td>
                </Table.Tr>
                <Table.Tr>
                  <Table.Td><Text fw={500}>Capacity Utilization</Text></Table.Td>
                  <Table.Td style={{ textAlign: 'right' }}>{PHANTOM_METRICS.capacityUtilization.current}%</Table.Td>
                  <Table.Td style={{ textAlign: 'right' }}>{formatChange(PHANTOM_METRICS.capacityUtilization.DoD)}</Table.Td>
                  <Table.Td style={{ textAlign: 'right' }}>{formatChange(PHANTOM_METRICS.capacityUtilization.WoW)}</Table.Td>
                  <Table.Td style={{ textAlign: 'right' }}>{formatChange(PHANTOM_METRICS.capacityUtilization.MoM)}</Table.Td>
                  <Table.Td style={{ textAlign: 'right' }}>{formatChange(PHANTOM_METRICS.capacityUtilization.QoQ)}</Table.Td>
                  <Table.Td style={{ textAlign: 'right' }}>{formatChange(PHANTOM_METRICS.capacityUtilization.YoY)}</Table.Td>
                </Table.Tr>
                <Table.Tr>
                  <Table.Td><Text fw={500}>Lead Time (days)</Text></Table.Td>
                  <Table.Td style={{ textAlign: 'right' }}>{PHANTOM_METRICS.leadTimeDays.current}</Table.Td>
                  <Table.Td style={{ textAlign: 'right' }}>{formatChange(PHANTOM_METRICS.leadTimeDays.DoD, true)}</Table.Td>
                  <Table.Td style={{ textAlign: 'right' }}>{formatChange(PHANTOM_METRICS.leadTimeDays.WoW, true)}</Table.Td>
                  <Table.Td style={{ textAlign: 'right' }}>{formatChange(PHANTOM_METRICS.leadTimeDays.MoM, true)}</Table.Td>
                  <Table.Td style={{ textAlign: 'right' }}>{formatChange(PHANTOM_METRICS.leadTimeDays.QoQ, true)}</Table.Td>
                  <Table.Td style={{ textAlign: 'right' }}>{formatChange(PHANTOM_METRICS.leadTimeDays.YoY, true)}</Table.Td>
                </Table.Tr>
                <Table.Tr>
                  <Table.Td><Text fw={500}>Warranty Rate</Text></Table.Td>
                  <Table.Td style={{ textAlign: 'right' }}>{PHANTOM_METRICS.warrantyRate.current}%</Table.Td>
                  <Table.Td style={{ textAlign: 'right' }}>{formatChange(PHANTOM_METRICS.warrantyRate.DoD, true)}</Table.Td>
                  <Table.Td style={{ textAlign: 'right' }}>{formatChange(PHANTOM_METRICS.warrantyRate.WoW, true)}</Table.Td>
                  <Table.Td style={{ textAlign: 'right' }}>{formatChange(PHANTOM_METRICS.warrantyRate.MoM, true)}</Table.Td>
                  <Table.Td style={{ textAlign: 'right' }}>{formatChange(PHANTOM_METRICS.warrantyRate.QoQ, true)}</Table.Td>
                  <Table.Td style={{ textAlign: 'right' }}>{formatChange(PHANTOM_METRICS.warrantyRate.YoY, true)}</Table.Td>
                </Table.Tr>
              </Table.Tbody>
            </Table>
          </Card>
        </Stack>
      </div>
    </div>
  );
}
